import * as React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Save, Smartphone } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  PageHeader,
  PageHeaderDescription,
  PageHeaderHeading,
  PageHeaderTitle,
  PageHeaderTop,
  RadioGroup,
  RadioGroupItem,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import type {
  LearnerAppConfigUpdateRequest,
  LearnerAppPaymentOwner,
  LearnerAppPricingMode,
} from "@eduviaos/shared";
import {
  formatLearnerAppPrice,
  useLearnerAppConfig,
  useUpdateLearnerAppConfig,
} from "@/hooks/useLearnerAppConfig";

/** Modes an institution admin may choose. `sa_push` is Super Admin only. */
const IA_PRICING_MODES: ReadonlyArray<{
  value: Exclude<LearnerAppPricingMode, "sa_push">;
  label: string;
  description: string;
}> = [
  {
    value: "bundled",
    label: "Bundled with seat",
    description: "App access is included when a learner has a seat.",
  },
  {
    value: "separate",
    label: "Separate monthly fee",
    description: "Charge a monthly app fee; grant access from the roster after payment.",
  },
];

function rupeesToPaise(input: string): number {
  const parsed = Number.parseFloat(input.replace(/,/g, "").trim());
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
}

function paiseToRupeeField(paise: number): string {
  if (paise <= 0) return "";
  return String(paise / 100);
}

export function LearnerAppSettingsPage() {
  const configQuery = useLearnerAppConfig();
  const saveMutation = useUpdateLearnerAppConfig();

  const [pricingMode, setPricingMode] =
    React.useState<Exclude<LearnerAppPricingMode, "sa_push">>("bundled");
  const [monthlyRupees, setMonthlyRupees] = React.useState("");
  const [dirty, setDirty] = React.useState(false);
  const [paymentOwner, setPaymentOwner] = React.useState<LearnerAppPaymentOwner>("eduviaos");
  const [centreKeyId, setCentreKeyId] = React.useState("");
  const [centreKeySecret, setCentreKeySecret] = React.useState("");
  const [centreWebhookSecret, setCentreWebhookSecret] = React.useState("");
  const [paymentDirty, setPaymentDirty] = React.useState(false);
  const platformManaged = configQuery.data?.pricing_mode === "sa_push";

  React.useEffect(() => {
    if (!configQuery.data) return;
    setPaymentOwner(configQuery.data.payment_owner);
    setCentreKeyId(configQuery.data.razorpay_key_id);
    setCentreKeySecret("");
    setCentreWebhookSecret("");
    setPaymentDirty(false);
    if (configQuery.data.pricing_mode === "sa_push") {
      setDirty(false);
      return;
    }
    setPricingMode(configQuery.data.pricing_mode);
    setMonthlyRupees(paiseToRupeeField(configQuery.data.monthly_price_paise));
    setDirty(false);
  }, [configQuery.data]);

  const monthlyPaise = rupeesToPaise(monthlyRupees);
  const body: LearnerAppConfigUpdateRequest = {
    pricing_mode: pricingMode,
    monthly_price_paise: pricingMode === "bundled" ? 0 : monthlyPaise,
    commission_bps: configQuery.data?.commission_bps ?? 0,
  };

  const canSave =
    dirty && !saveMutation.isPending && (pricingMode === "bundled" || monthlyPaise > 0);

  const secretAlreadySet = configQuery.data?.razorpay_key_secret_set === true;
  const canSavePayment =
    paymentDirty &&
    !saveMutation.isPending &&
    Boolean(configQuery.data) &&
    (paymentOwner === "eduviaos" ||
      (centreKeyId.trim().length > 0 && (secretAlreadySet || centreKeySecret.trim().length > 0)));

  async function handleSave() {
    try {
      await saveMutation.mutateAsync(body);
      toast.success("Learner app pricing updated.");
      setDirty(false);
    } catch {
      toast.error("Couldn't save pricing. Try again in a moment.");
    }
  }

  async function handleSavePayment() {
    if (!configQuery.data) return;
    const paymentBody: LearnerAppConfigUpdateRequest = {
      pricing_mode: configQuery.data.pricing_mode,
      monthly_price_paise: configQuery.data.monthly_price_paise,
      commission_bps: configQuery.data.commission_bps,
      payment_owner: paymentOwner,
    };
    if (paymentOwner === "centre") {
      paymentBody.razorpay_key_id = centreKeyId.trim();
      if (centreKeySecret.trim()) paymentBody.razorpay_key_secret = centreKeySecret.trim();
      if (centreWebhookSecret.trim()) {
        paymentBody.razorpay_webhook_secret = centreWebhookSecret.trim();
      }
    }
    try {
      await saveMutation.mutateAsync(paymentBody);
      toast.success("Payment collection saved.");
      setCentreKeySecret("");
      setCentreWebhookSecret("");
      setPaymentDirty(false);
    } catch {
      toast.error("Couldn't save payment collection. Try again.");
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-4 pb-12 lg:p-8">
      <PageHeader>
        <PageHeaderTop>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="size-4" aria-hidden="true" />
              Settings
            </Link>
          </Button>
        </PageHeaderTop>
        <PageHeaderHeading>
          <PageHeaderTitle>Learner app</PageHeaderTitle>
          <PageHeaderDescription>
            How your centre prices the learner app. When pricing is separate, use the roster to
            grant access after a learner pays at the front desk.
          </PageHeaderDescription>
        </PageHeaderHeading>
      </PageHeader>

      {configQuery.isLoading ? (
        <Card>
          <CardContent className="text-muted-foreground flex items-center gap-2 py-8 text-sm">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            Loading…
          </CardContent>
        </Card>
      ) : configQuery.isError ? (
        <Card>
          <CardContent className="text-destructive py-8 text-sm">
            Learner app settings are only available for study centres.
          </CardContent>
        </Card>
      ) : platformManaged && configQuery.data ? (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Smartphone className="text-muted-foreground size-5" aria-hidden="true" />
              <CardTitle>Pricing mode</CardTitle>
            </div>
            <CardDescription>Your EduViaOS account team sets this price.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="border-border/60 bg-muted/30 grid gap-1 rounded-lg border p-3">
              <p className="text-sm font-medium">Platform-recommended price</p>
              <p className="text-sm tabular-nums">
                {formatLearnerAppPrice(configQuery.data.monthly_price_paise)} / month
              </p>
              <p className="text-muted-foreground text-xs">
                Your team still grants access from the roster after a learner pays. There is nothing
                to save here.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Smartphone className="text-muted-foreground size-5" aria-hidden="true" />
              <CardTitle>Pricing mode</CardTitle>
            </div>
            <CardDescription>
              {pricingMode === "bundled"
                ? "Learners with portal access can use the app immediately."
                : "Learners see a paywall until you grant app access from People → Learners."}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <RadioGroup
              value={pricingMode}
              onValueChange={(next) => {
                setPricingMode(next as Exclude<LearnerAppPricingMode, "sa_push">);
                setDirty(true);
              }}
              className="grid gap-3"
            >
              {IA_PRICING_MODES.map((mode) => (
                <label
                  key={mode.value}
                  className={cn(
                    "border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3",
                    pricingMode === mode.value && "border-primary/40 bg-muted/30",
                  )}
                >
                  <RadioGroupItem value={mode.value} className="mt-0.5" />
                  <div className="grid gap-1">
                    <span className="text-sm font-medium">{mode.label}</span>
                    <span className="text-muted-foreground text-xs">{mode.description}</span>
                  </div>
                </label>
              ))}
            </RadioGroup>

            {pricingMode !== "bundled" ? (
              <div className="grid max-w-xs gap-2">
                <Label htmlFor="ia-learner-app-monthly">Monthly price (₹)</Label>
                <Input
                  id="ia-learner-app-monthly"
                  inputMode="decimal"
                  value={monthlyRupees}
                  onChange={(e) => {
                    setMonthlyRupees(e.target.value);
                    setDirty(true);
                  }}
                />
                {monthlyPaise > 0 ? (
                  <p className="text-muted-foreground text-xs">
                    Paywall shows {formatLearnerAppPrice(monthlyPaise)} / month.
                  </p>
                ) : null}
              </div>
            ) : null}

            <Button type="button" disabled={!canSave} onClick={() => void handleSave()}>
              {saveMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-4" aria-hidden="true" />
              )}
              Save
            </Button>
          </CardContent>
        </Card>
      )}

      {!configQuery.isLoading && !configQuery.isError && configQuery.data ? (
        <Card>
          <CardHeader>
            <CardTitle>Who collects app payments</CardTitle>
            <CardDescription>
              EduViaOS can charge learners with its Razorpay account, or your centre can use its
              own.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-6">
            <RadioGroup
              value={paymentOwner}
              onValueChange={(next) => {
                setPaymentOwner(next as LearnerAppPaymentOwner);
                setPaymentDirty(true);
              }}
              className="grid gap-3"
            >
              <label className="border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3">
                <RadioGroupItem value="eduviaos" className="mt-0.5" />
                <div className="grid gap-1">
                  <span className="text-sm font-medium">EduViaOS Razorpay</span>
                  <span className="text-muted-foreground text-xs">
                    Learners pay the EduViaOS account. You do not enter a key.
                  </span>
                </div>
              </label>
              <label className="border-border/60 flex cursor-pointer items-start gap-3 rounded-lg border p-3">
                <RadioGroupItem value="centre" className="mt-0.5" />
                <div className="grid gap-1">
                  <span className="text-sm font-medium">Our Razorpay</span>
                  <span className="text-muted-foreground text-xs">
                    Learners pay this centre&apos;s Razorpay account.
                  </span>
                </div>
              </label>
            </RadioGroup>
            {paymentOwner === "centre" ? (
              <div className="grid max-w-md gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="ia-centre-razorpay-key">Razorpay Key ID</Label>
                  <Input
                    id="ia-centre-razorpay-key"
                    autoComplete="off"
                    placeholder="rzp_test_…"
                    value={centreKeyId}
                    onChange={(event) => {
                      setCentreKeyId(event.target.value);
                      setPaymentDirty(true);
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ia-centre-razorpay-secret">Key Secret</Label>
                  <Input
                    id="ia-centre-razorpay-secret"
                    type="password"
                    autoComplete="new-password"
                    placeholder={secretAlreadySet ? "Saved. Enter a new secret to replace it." : ""}
                    value={centreKeySecret}
                    onChange={(event) => {
                      setCentreKeySecret(event.target.value);
                      setPaymentDirty(true);
                    }}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ia-centre-razorpay-webhook">Webhook secret</Label>
                  <Input
                    id="ia-centre-razorpay-webhook"
                    type="password"
                    autoComplete="new-password"
                    placeholder={
                      configQuery.data.razorpay_webhook_secret_set
                        ? "Saved. Enter a new secret to replace it."
                        : "Optional"
                    }
                    value={centreWebhookSecret}
                    onChange={(event) => {
                      setCentreWebhookSecret(event.target.value);
                      setPaymentDirty(true);
                    }}
                  />
                </div>
              </div>
            ) : null}
            <Button
              type="button"
              disabled={!canSavePayment}
              onClick={() => void handleSavePayment()}
            >
              {saveMutation.isPending ? (
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="size-4" aria-hidden="true" />
              )}
              Save collection
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
